/**
 * Main-thread half of the engine voice: loads the worklet module, builds the
 * node, and gives `engine.ts` a tiny synchronous API that is safe to call
 * before (and after) the module has finished loading.
 *
 * **Fallback policy.** If `AudioWorklet` is unavailable, or the module fails to
 * load, the engine layer is **silent** and says so once. It does not fall back
 * to the parked procedural synth (`engine.ts`'s `createEngineSynth`): that
 * circuit was built before the original's engine sound was understood and
 * `docs/engineering/audio-plan.md` §6 supersedes it outright, so reviving it on
 * an error path would mean shipping the known-wrong sound to exactly the users
 * who can least tell why. Nor does it fall back to the "Option A" crossfaded
 * ladder of pre-rendered buffers §6 rejects; that stays a documented option, not
 * an implemented one.
 *
 * Every browser this port targets (WebGPU-capable, per AD-001) has had
 * `AudioWorklet` for years, so the fallback is a diagnostic path rather than a
 * supported mode — hence a console message naming the cause rather than a
 * silent degradation nobody can debug.
 */

import {
  ENGINE_INDEX_MIN,
  ENGINE_VOICE_PROCESSOR,
  ENGINE_VOICE_WORKLET_URL,
  type EngineVoiceCommand,
} from "./engine-voice.ts";

export interface EngineVoiceHandle {
  /** Pitch index the RPM state should head for; ignored while the module is still loading except for the latest value. */
  setTargetIndex(index: number): void;
  /** Rung of the distance ladder for the nearest other aircraft, or `null` to key that voice off. */
  setEnemyLevelIndex(index: number | null): void;
  /** True once the worklet node is live on the bus — the browser-verification hook and what the tests assert. */
  readonly active: boolean;
  dispose(): void;
}

/** The one message the fallback path prints, kept as a constant so a test can assert it is reached. */
export const ENGINE_WORKLET_UNAVAILABLE_MESSAGE =
  "red-opus audio: AudioWorklet unavailable — the engine voice is silent for this session. " +
  "It is a real OPL2 voice rendered on the audio thread (docs/engineering/audio-plan.md §6); there is no synthesised substitute.";

function reportUnavailable(cause: unknown): void {
  const detail = cause instanceof Error ? ` (${cause.message})` : "";
  console.warn(`${ENGINE_WORKLET_UNAVAILABLE_MESSAGE}${detail}`);
}

/** Silent stand-in with the same shape, so callers never branch on whether audio came up. */
const SILENT_HANDLE: EngineVoiceHandle = {
  setTargetIndex() {},
  setEnemyLevelIndex() {},
  active: false,
  dispose() {},
};

class WorkletEngineVoice implements EngineVoiceHandle {
  private node: AudioWorkletNode | null = null;
  private disposed = false;
  /** Last value of each field, so a 60 Hz `updateFrame` only posts when something actually moved. */
  private lastTargetIndex = ENGINE_INDEX_MIN;
  private lastEnemyLevelIndex: number | null = null;

  constructor(
    private readonly context: BaseAudioContext,
    private readonly bus: AudioNode,
  ) {}

  get active(): boolean {
    return this.node !== null;
  }

  /**
   * Loads the module and attaches the node. Resolves either way — a failure is
   * reported and leaves the handle silent, never rejected into a caller that
   * has no better answer than this one does.
   */
  async start(): Promise<void> {
    try {
      await this.context.audioWorklet.addModule(ENGINE_VOICE_WORKLET_URL);
      if (this.disposed) return;
      const node = new AudioWorkletNode(this.context, ENGINE_VOICE_PROCESSOR, { numberOfInputs: 0, outputChannelCount: [1] });
      node.connect(this.bus);
      this.node = node;
      // The voice has been running since the processor's constructor; catch it up to whatever the frame loop set meanwhile.
      this.post({ targetIndex: this.lastTargetIndex, enemyLevelIndex: this.lastEnemyLevelIndex });
    } catch (cause) {
      reportUnavailable(cause);
    }
  }

  setTargetIndex(index: number): void {
    if (index === this.lastTargetIndex) return;
    this.lastTargetIndex = index;
    this.post({ targetIndex: index });
  }

  setEnemyLevelIndex(index: number | null): void {
    if (index === this.lastEnemyLevelIndex) return;
    this.lastEnemyLevelIndex = index;
    this.post({ enemyLevelIndex: index });
  }

  private post(command: EngineVoiceCommand): void {
    if (this.disposed) return;
    this.node?.port.postMessage(command);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.node?.disconnect();
    this.node = null;
  }
}

/**
 * Starts the engine voice on `bus`. Returns synchronously with a usable handle;
 * the module load continues in the background and the node joins the graph when
 * it lands (or never, and the handle stays silent).
 */
export function createEngineVoice(context: BaseAudioContext, bus: AudioNode): EngineVoiceHandle {
  if (typeof AudioWorkletNode !== "function" || typeof context.audioWorklet?.addModule !== "function") {
    reportUnavailable(new Error("this AudioContext has no audioWorklet"));
    return SILENT_HANDLE;
  }
  const voice = new WorkletEngineVoice(context, bus);
  void voice.start();
  return voice;
}
